import { TOOL_INDEX, type ToolContext, type ToolDef } from './toolRegistry';
import { formatUnknownError } from './errorMessage';

export type DispatchResult =
  | { ok: true; result: unknown; toolName: string; cancelled?: false }
  | {
      ok: false;
      error: string;
      code: 'unknown_tool' | 'requires_premium' | 'cancelled' | 'timeout' | 'busy' | 'failed';
      toolName: string;
      upgrade_url?: string;
    };

/** Non-interactive tools must finish within this window. Interactive ones are user-driven, no timeout. */
const NON_INTERACTIVE_TIMEOUT_MS = 20_000;

async function executeImpl(
  name: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
  signal?: AbortSignal,
): Promise<DispatchResult> {
  const tool: ToolDef | undefined = name in TOOL_INDEX ? TOOL_INDEX[name] : undefined;
  if (!tool) {
    return { ok: false, error: `Unknown tool: ${name}`, code: 'unknown_tool', toolName: name };
  }

  if (tool.requiresPlan === 'premium' && ctx.plan !== 'premium') {
    return {
      ok: false,
      error: 'This tool requires a premium plan.',
      code: 'requires_premium',
      toolName: name,
      upgrade_url: '/plans',
    };
  }

  if (signal?.aborted) {
    return { ok: false, error: 'Cancelled before start.', code: 'cancelled', toolName: name };
  }

  try {
    if (tool.interactive) {
      // ModalHostContext enforces single-modal FIFO — reject overlapping
      // dispatches so a hallucinated double call doesn't queue a second modal.
      if (ctx.modal.active) {
        return { ok: false, error: 'Another action is already open.', code: 'busy', toolName: name };
      }

      let result: unknown;
      try {
        const openPromise = ctx.modal.openScreen(tool.screen, args);
        if (signal) {
          // If the page aborts mid-modal, close everything and resolve cancelled.
          const onAbort = () => { ctx.modal.closeAll(); };
          signal.addEventListener('abort', onAbort, { once: true });
          try {
            result = await openPromise;
          } finally {
            signal.removeEventListener('abort', onAbort);
          }
        } else {
          result = await openPromise;
        }
      } catch (error: unknown) {
        if (error && typeof error === 'object' && (error as { cancelled?: boolean }).cancelled) {
          return { ok: false, error: 'User cancelled.', code: 'cancelled', toolName: name };
        }
        throw error;
      }

      if (tool.onComplete) {
        try { await tool.onComplete(result, ctx, args); } catch { /* best-effort */ }
      }

      const shaped = tool.shape ? tool.shape(result, ctx, args) : result;
      return { ok: true, result: shaped, toolName: name };
    }

    // Non-interactive: race against an abort signal AND a 20s timeout.
    const handlerPromise = tool.handler(args, ctx);
    const guards: Promise<DispatchResult>[] = [];

    const timeoutState: { id: ReturnType<typeof setTimeout> | undefined } = { id: undefined };
    guards.push(
      new Promise<DispatchResult>((resolve) => {
        timeoutState.id = setTimeout(
          () => { resolve({ ok: false, error: 'Tool timed out.', code: 'timeout', toolName: name }); },
          NON_INTERACTIVE_TIMEOUT_MS,
        );
      }),
    );

    if (signal) {
      guards.push(
        new Promise<DispatchResult>((resolve) => {
          const onAbort = () =>
            { resolve({ ok: false, error: 'User cancelled.', code: 'cancelled', toolName: name }); };
          if (signal.aborted) onAbort();
          else signal.addEventListener('abort', onAbort, { once: true });
        }),
      );
    }

    const wrappedHandler: Promise<DispatchResult> = handlerPromise.then(
      (handlerResult) => ({ ok: true as const, result: handlerResult, toolName: name }),
      (error: unknown) => ({
        ok: false as const,
        error: formatUnknownError(error),
        code: 'failed' as const,
        toolName: name,
      }),
    );

    const winner = await Promise.race([wrappedHandler, ...guards]);
    if (timeoutState.id !== undefined) clearTimeout(timeoutState.id);
    return winner;
  } catch (error: unknown) {
    return {
      ok: false,
      error: formatUnknownError(error),
      code: 'failed',
      toolName: name,
    };
  }
}

export async function dispatchTool(
  name: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
  signal?: AbortSignal,
): Promise<DispatchResult> {
  return executeImpl(name, args, ctx, signal);
}

/**
 * Encode a dispatch result as the `<tool_result>...</tool_result>` payload the
 * worker feeds back to the model. Errors are encoded as JSON errors so the
 * model can react conversationally rather than crashing the chat loop.
 */
export function encodeToolResult(r: DispatchResult): string {
  if (r.ok) {
    return `<tool_result>${JSON.stringify(r.result ?? {})}</tool_result>`;
  }
  const payload: Record<string, unknown> = { error: r.error, code: r.code };
  if (r.upgrade_url) payload.upgrade_url = r.upgrade_url;
  return `<tool_result>${JSON.stringify(payload)}</tool_result>`;
}
